import React, {useEffect, useState} from 'react';
import {Form, ListGroup, Spinner} from 'react-bootstrap';
import {useIntl} from 'react-intl';
import {useDebounce} from '../../../_metronic/helpers';
import {useGetAdresseSuggestions} from '../Hooks/DivopsHooks';
import {AdresseSuggestion, GpsCoordinates} from '../Types/types';

const GPS_REGEX = /^(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)$/;

const parseGpsInput = (value: string): GpsCoordinates | null => {
  const match = GPS_REGEX.exec(value.trim());
  if (!match) return null;
  return {latitude: parseFloat(match[1]), longitude: parseFloat(match[2])};
};

const isLatitudeValid = (lat: number) => lat >= -90 && lat <= 90;
const isLongitudeValid = (lon: number) => lon >= -180 && lon <= 180;

interface AdresseAutocompleteProps {
  onSelect: (suggestion: AdresseSuggestion) => void;
  onSelectGps?: (coordinates: GpsCoordinates) => void;
  placeholder?: string;
  initialValue?: string;
}

const AdresseAutocomplete = ({onSelect, onSelectGps, placeholder, initialValue = ''}: AdresseAutocompleteProps): JSX.Element => {
  const intl = useIntl();
  const [inputValue, setInputValue] = useState(initialValue);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const debouncedValue = useDebounce(inputValue, 300) ?? '';

  const gpsCandidate = parseGpsInput(debouncedValue);
  const gpsError = gpsCandidate !== null && (!isLatitudeValid(gpsCandidate.latitude) || !isLongitudeValid(gpsCandidate.longitude));
  const isValidGps = gpsCandidate !== null && !gpsError;

  const {data: suggestions = [], isFetching} = useGetAdresseSuggestions(gpsCandidate ? '' : debouncedValue);

  useEffect(() => {
    if (isValidGps && gpsCandidate) {
      onSelectGps?.(gpsCandidate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isValidGps, gpsCandidate?.latitude, gpsCandidate?.longitude]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    setShowSuggestions(true);
  };

  const handleSelect = (suggestion: AdresseSuggestion) => {
    setInputValue(suggestion.label);
    onSelect(suggestion);
    setShowSuggestions(false);
  };

  return (
    <div className="position-relative">
      <Form.Control
        type="text"
        placeholder={placeholder}
        value={inputValue}
        onChange={handleInputChange}
        onFocus={() => setShowSuggestions(true)}
        onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
        autoComplete="off"
        className="form-control-lg form-control-solid"
      />

      {gpsError && (
        <div className="text-danger fs-8 mt-1">
          {intl.formatMessage({id: 'DIVOPS.ELIGIBILITY.ADRESSE.GPS_INVALID'})}
        </div>
      )}

      {showSuggestions && !gpsCandidate && debouncedValue.length >= 3 && (
        <ListGroup
          className="position-absolute w-100 mt-1 shadow-sm"
          style={{zIndex: 1000, maxHeight: '300px', overflowY: 'auto'}}
        >
          {isFetching ? (
            <ListGroup.Item className="d-flex align-items-center justify-content-center py-3">
              <Spinner animation="border" size="sm" className="me-2" />
              {intl.formatMessage({id: 'WAIT'})}
            </ListGroup.Item>
          ) : suggestions.length > 0 ? (
            suggestions.map((suggestion, index) => (
              <ListGroup.Item
                key={`${suggestion.codeInsee}-${suggestion.label}-${index}`}
                action
                onClick={() => handleSelect(suggestion)}
                className="cursor-pointer"
              >
                {suggestion.label}
              </ListGroup.Item>
            ))
          ) : (
            <ListGroup.Item>{intl.formatMessage({id: 'FIELD.NO.OPTIONS'})}</ListGroup.Item>
          )}
        </ListGroup>
      )}
    </div>
  );
};

export default AdresseAutocomplete;
